import { z } from 'zod';

/**
 * Zod schema for individual contact validation
 */
export const ContactSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(100, 'Name must be 100 characters or less')
    .trim(),
  phoneNumber: z
    .string()
    .min(1, 'Phone number is required')
    .regex(/^[0-9]+$/, 'Phone number must contain only digits')
    .min(10, 'Phone number must be at least 10 digits')
    .max(15, 'Phone number must be at most 15 digits')
    .trim(),
});

/**
 * filter schema
 */
const FilterSchema = z.object({
  field: z.string().optional().or(z.literal('')),
  operator: z.string().optional().or(z.literal('')),
  value: z.string().optional().or(z.literal('')),
});

/**
 * Main broadcast creation schema
 */
export const CreateBroadcastSchema = z
  .object({
    name: z
      .string()
      .min(3, 'Name must be at least 3 characters')
      .max(100, 'Name must be 100 characters or less')
      .trim(),
    description: z.string().optional(),
    templateId: z.string().min(1, 'Template selection is required'),
    executionType: z.enum(['immediate', 'scheduled']).default('immediate'),
    scheduledAt: z.string().optional().or(z.literal('')),
    contactSelectionMethod: z
      .enum(['uploaded', 'filtered', 'all'])
      .default('uploaded'),
    saveContacts: z.boolean().default(false),
    contacts: z.array(ContactSchema).optional(),
    filters: z.array(FilterSchema).optional(),
    filterLogic: z.enum(['AND', 'OR']).default('AND'),

    // Dynamic parameters (highly template-dependent, validated as objects/arrays)
    dynamicValues: z
      .object({
        header: z.any().optional(),
        body: z.array(z.any()).optional(),
        buttons: z.array(z.any()).optional(),
      })
      .optional(),
  })
  .refine(
    data => {
      if (data.executionType === 'scheduled') {
        if (!data.scheduledAt) return false;
        const scheduledDate = new Date(data.scheduledAt);
        const minDate = new Date(Date.now() + 19 * 60 * 1000); // ~20 mins future
        const maxDate = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000);
        return scheduledDate >= minDate && scheduledDate <= maxDate;
      }
      return true;
    },
    {
      message:
        'Scheduled time must be between 20 minutes and 90 days in the future',
      path: ['scheduledAt'],
    }
  )
  .refine(
    data => {
      if (data.contactSelectionMethod === 'uploaded') {
        return data.contacts && data.contacts.length > 0;
      }
      return true;
    },
    {
      message: 'At least one contact is required',
      path: ['contacts'],
    }
  )
  .refine(
    data => {
      if (data.contactSelectionMethod === 'filtered') {
        return (
          data.filters &&
          data.filters.length > 0 &&
          data.filters.some(f => f.field && f.operator)
        );
      }
      return true;
    },
    {
      message: 'At least one filter rule is required',
      path: ['filters'],
    }
  );
